'use client';

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  MicroCardFlipFaces,
  MicroDragDropExercise,
  MicroMatchingExercise,
  MicroProgress,
} from '@akademi/site-builder-runtime';
import type { RendererProps } from '../types';
import { ErrorState, PopupHeader, btnPrimary, btnSecondary, padded } from '../chrome';
import { resolveInteractiveKind } from './interactiveKind';

/**
 * Multi-variant interactive renderer dispatching on
 * {@code body.interactiveKind} (the field the admin editor writes):
 *
 * <ul>
 *   <li>{@code card-flip} — a deck of two-faced cards ({@code body.cards});
 *       completion once every card's back has been viewed</li>
 *   <li>{@code matching} — {@code body.matching} left prompts matched to
 *       right options; {@code matchOrder[i]} is the right-index for
 *       {@code left[i]}</li>
 *   <li>{@code reflection} — open-text reflection</li>
 *   <li>{@code drag-drop} — sort items into their target zones
 *       ({@code body.dragDrop})</li>
 * </ul>
 *
 * The variant comes from {@link resolveInteractiveKind}: the explicit
 * discriminator when present, otherwise the one canonical sub-body the
 * admin wrote (rows saved before the editor stamped the default kind).
 * Anything still ambiguous renders the honest error state — guessing
 * between two variants would play the wrong exercise.
 */
export function InteractiveRenderer({ item, onComplete, progress }: RendererProps) {
  const { t } = useTranslation('site-builder-blocks');
  const body =
    (item.body as {
      interactiveKind?: string;
      cards?: MicroCardFlipFaces[];
      matching?: MicroMatchingExercise;
      dragDrop?: MicroDragDropExercise;
      reflection?: ReflectionConfig;
    }) ?? {};
  const subKind = resolveInteractiveKind(body);

  if (subKind === 'card-flip') {
    return <CardFlip item={item} cards={body.cards ?? []} onComplete={onComplete} />;
  }
  if (subKind === 'matching') {
    return (
      <Matching item={item} matching={body.matching} progress={progress} onComplete={onComplete} />
    );
  }
  if (subKind === 'reflection') {
    return (
      <Reflection
        item={item}
        config={body.reflection}
        progress={progress}
        onComplete={onComplete}
      />
    );
  }
  if (subKind === 'drag-drop') {
    return (
      <DragDrop item={item} exercise={body.dragDrop} progress={progress} onComplete={onComplete} />
    );
  }
  return <ErrorState message={t('microInteractive.noKind')} />;
}

function CardFlip({
  item,
  cards,
  onComplete,
}: {
  item: RendererProps['item'];
  cards: MicroCardFlipFaces[];
  onComplete: () => void;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const seenBacksRef = useRef(new Set<number>());
  const completeFiredRef = useRef(false);
  const card = cards[idx];

  if (!card) {
    return (
      <div style={padded}>
        <PopupHeader kind={item.kind} title={item.title} />
        <div style={{ color: 'var(--color-text-secondary, #71717a)', marginTop: 16 }}>
          {t('microInteractive.cardFlip.empty')}
        </div>
      </div>
    );
  }

  const flip = () => {
    const next = !flipped;
    setFlipped(next);
    if (!next) return;
    seenBacksRef.current.add(idx);
    // Seeing every back = the exercise's payoff; fire once.
    if (!completeFiredRef.current && seenBacksRef.current.size >= cards.length) {
      completeFiredRef.current = true;
      onComplete();
    }
  };

  // A new card always starts face up.
  const goTo = (next: number) => {
    setIdx(next);
    setFlipped(false);
  };
  const deck = cards.length > 1;

  // A real flip: both faces are stacked in one grid cell and the stack
  // rotates around Y in 3D. The back starts pre-rotated with its
  // backface hidden, so the front rotates away as the back rotates in.
  const faceStyle = (side: 'front' | 'back'): CSSProperties => ({
    gridArea: '1 / 1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: 'clamp(24px, 4vw, 40px)',
    minHeight: 240,
    borderRadius: 16,
    border: `2px solid ${
      side === 'front' ? 'var(--color-brand-200, #c2d3f7)' : 'var(--color-success-500, #16a34a)'
    }`,
    background:
      side === 'front'
        ? 'linear-gradient(160deg, var(--color-brand-50, #eff6ff), var(--color-brand-100, #dbeafe))'
        : 'linear-gradient(160deg, #f0fdf4, #dcfce7)',
    boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12)',
    backfaceVisibility: 'hidden',
    WebkitBackfaceVisibility: 'hidden',
    transform: side === 'back' ? 'rotateY(180deg)' : 'rotateY(0deg)',
    textAlign: 'center',
    lineHeight: 1.45,
  });

  const cornerLabel = (label: string, color: string) => (
    <span
      aria-hidden
      style={{
        position: 'absolute',
        top: 12,
        left: 14,
        fontSize: '11px',
        fontWeight: 800,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color,
      }}
    >
      {label}
    </span>
  );

  const Face = ({ side }: { side: 'front' | 'back' }) => {
    const title = side === 'front' ? card.frontTitle : card.backTitle;
    const text = side === 'front' ? card.frontText : card.backText;
    return (
      <div
        style={{ ...faceStyle(side), position: 'relative' }}
        aria-hidden={flipped ? side === 'front' : side === 'back'}
      >
        {cornerLabel(
          side === 'front'
            ? t('microInteractive.cardFlip.front')
            : t('microInteractive.cardFlip.back'),
          side === 'front'
            ? 'var(--color-brand-600, #2563eb)'
            : 'var(--color-success-500, #16a34a)',
        )}
        {title && (
          <span style={{ fontSize: 'var(--font-size-xl, 22px)', fontWeight: 700 }}>{title}</span>
        )}
        {text && <span style={{ fontSize: 'var(--font-size-md, 16px) ' }}>{text}</span>}
        <span
          aria-hidden
          style={{
            marginTop: 12,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 'var(--font-size-xs, 12px)',
            fontWeight: 600,
            color: 'var(--color-text-tertiary, #94867d)',
          }}
        >
          <FlipIcon />
          {side === 'front'
            ? t('microInteractive.cardFlip.hintFront')
            : t('microInteractive.cardFlip.hintBack')}
        </span>
      </div>
    );
  };

  return (
    <>
      <PopupHeader
        kind={item.kind}
        title={item.title}
        description={item.description}
        meta={
          deck ? (
            <div
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                color: 'var(--color-text-tertiary, #94867d)',
              }}
            >
              {t('microInteractive.cardFlip.counter', { current: idx + 1, total: cards.length })}
            </div>
          ) : undefined
        }
      />
      <div style={{ ...padded, perspective: 1400 }}>
        {/* Keyed by card so moving on remounts face up instead of animating
            the next card's back into view. */}
        <button
          key={idx}
          type="button"
          onClick={flip}
          aria-pressed={flipped}
          aria-label={
            flipped
              ? t('microInteractive.cardFlip.hintBack')
              : t('microInteractive.cardFlip.hintFront')
          }
          style={{
            width: '100%',
            display: 'grid',
            padding: 0,
            border: 'none',
            background: 'transparent',
            font: 'inherit',
            color: 'inherit',
            cursor: 'pointer',
            transformStyle: 'preserve-3d',
            transition: 'transform 650ms cubic-bezier(0.4, 0.2, 0.2, 1)',
            transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
          }}
        >
          <Face side="front" />
          <Face side="back" />
        </button>
        {deck && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
            <button
              type="button"
              onClick={() => goTo(idx - 1)}
              disabled={idx === 0}
              style={btnSecondary}
            >
              {t('microInteractive.cardFlip.previous')}
            </button>
            <button
              type="button"
              onClick={() => goTo(idx + 1)}
              disabled={idx === cards.length - 1}
              style={btnPrimary}
            >
              {t('microInteractive.cardFlip.next')}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

function FlipIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1-6.4-2.6" />
      <path d="M3 12a9 9 0 0 1 9-9 9 9 0 0 1 6.4 2.6" />
      <path d="M3 18v-4h4" />
      <path d="M21 6v4h-4" />
    </svg>
  );
}

/**
 * The learner's stored matches — {@code result.matching.assignments},
 * one right-index per left row — when the progress row carries a set
 * that still fits this exercise. A stale key (the author edited the
 * rows since) is ignored rather than mis-rendered.
 */
function storedMatching(
  progress: MicroProgress | null | undefined,
  rows: number,
  options: number,
): number[] | null {
  const m = progress?.result?.['matching'];
  const a = m && typeof m === 'object' ? (m as { assignments?: unknown }).assignments : undefined;
  if (!Array.isArray(a) || a.length !== rows) return null;
  return a.every((v) => Number.isInteger(v) && v >= 0 && v < options) ? (a as number[]) : null;
}

function Matching({
  item,
  matching,
  progress,
  onComplete,
}: {
  item: RendererProps['item'];
  matching: MicroMatchingExercise | undefined;
  progress?: MicroProgress | null;
  onComplete: RendererProps['onComplete'];
}) {
  const { t } = useTranslation('site-builder-blocks');
  // Selected right-index per left row (absent = unmatched).
  const [assignments, setAssignments] = useState<Record<number, number>>({});
  // Answers are graded on demand, like drag-drop: grading each pick as it
  // is made would give the key away by elimination, and a button that only
  // wakes up once everything is right reads as broken to a learner whose
  // picks are wrong ("I matched them all — why is it disabled?").
  const [checked, setChecked] = useState(false);

  const left = matching?.left ?? [];
  const right = matching?.right ?? [];
  const matchOrder = matching?.matchOrder ?? [];

  // One submission per learner: a completed row's stored matches come
  // back read-only, so reopening the popup (or reloading) shows what
  // was sent instead of six empty pickers under a "completed" card.
  const stored = storedMatching(progress, left.length, right.length);
  const done = progress?.status === 'completed' && stored != null;
  const storedKey = stored ? stored.join(',') : null;
  useEffect(() => {
    if (!done || storedKey == null) return;
    setAssignments(Object.fromEntries(storedKey.split(',').map((v, i) => [i, Number(v)])));
    setChecked(true);
  }, [done, storedKey]);
  // A completable exercise needs rows, options, and a full in-range
  // answer key — anything else must never enable completion.
  const configValid =
    left.length > 0 &&
    right.length > 0 &&
    matchOrder.length === left.length &&
    matchOrder.every((m) => Number.isInteger(m) && m >= 0 && m < right.length);

  if (!configValid) {
    return (
      <>
        <PopupHeader kind={item.kind} title={item.title} description={item.description} />
        <ErrorState message={t('microInteractive.matching.invalid')} />
      </>
    );
  }

  const allFilled = left.every((_, i) => assignments[i] !== undefined);
  const isWrong = (i: number) => checked && assignments[i] !== matchOrder[i];
  const correctCount = left.filter((_, i) => assignments[i] === matchOrder[i]).length;
  const correct = correctCount === left.length;
  // Terminal when right: every row is matched and there is nothing left
  // to show, so the popup closes with the completion instead of lingering.
  // Otherwise mark the wrong rows and let the learner fix them.
  const check = () => {
    if (!allFilled || done) return;
    if (correct) {
      onComplete(
        { result: { matching: { assignments: left.map((_, i) => assignments[i]) } } },
        { close: true },
      );
    } else setChecked(true);
  };

  return (
    <>
      <PopupHeader kind={item.kind} title={item.title} description={item.description} />
      <div style={{ ...padded, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {matching?.instruction && (
          <p style={{ margin: 0, fontWeight: 600, fontSize: 'var(--font-size-sm, 14px)' }}>
            {matching.instruction}
          </p>
        )}
        {/* Two equal columns — prompt left, its picker right — so every
            row's select lines up regardless of the longest option; wraps
            to one column when the popup is too narrow for both. */}
        {left.map((prompt, i) => (
          <div key={i} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: '1 1 45%', minWidth: 140, fontWeight: 500 }}>{prompt}</div>
            <select
              value={assignments[i] ?? ''}
              aria-invalid={isWrong(i) || undefined}
              disabled={done}
              onChange={(e) =>
                setAssignments((a) => {
                  setChecked(false);
                  if (e.target.value === '') {
                    const { [i]: _cleared, ...rest } = a;
                    return rest;
                  }
                  return { ...a, [i]: Number(e.target.value) };
                })
              }
              style={{
                flex: '1 1 45%',
                minWidth: 140,
                padding: 'var(--space-2, 8px) var(--space-3, 12px)',
                border: `1px solid ${
                  isWrong(i)
                    ? 'var(--color-danger, #dc2626)'
                    : done
                      ? 'var(--color-success-500, #16a34a)'
                      : 'var(--color-border, rgba(0,0,0,0.12))'
                }`,
                boxShadow: isWrong(i) ? '0 0 0 2px rgba(220,38,38,0.15)' : 'none',
                borderRadius: 8,
                background: isWrong(i)
                  ? 'rgba(220,38,38,0.05)'
                  : done
                    ? 'rgba(22,163,74,0.06)'
                    : 'var(--color-surface-card, #fff)',
                color: 'inherit',
                opacity: 1,
                font: 'inherit',
              }}
            >
              <option value="">{t('microInteractive.matching.pick')}</option>
              {right.map((option, ri) => (
                <option key={ri} value={ri}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          {done ? (
            <SubmittedNote />
          ) : (
            <button
              type="button"
              onClick={check}
              disabled={!allFilled}
              style={{
                ...btnPri,
                opacity: allFilled ? 1 : 0.5,
                cursor: allFilled ? 'pointer' : 'not-allowed',
              }}
            >
              {!allFilled
                ? t('microInteractive.matching.hint')
                : checked
                  ? t('microInteractive.matching.retry')
                  : t('microInteractive.matching.check')}
            </button>
          )}
          {!done && checked && !correct && (
            <span
              role="status"
              style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-danger, #dc2626)' }}
            >
              {t('microInteractive.matching.someWrong', {
                correct: correctCount,
                total: left.length,
              })}
            </span>
          )}
        </div>
      </div>
    </>
  );
}

/** What the admin {@code ReflectionEditor} writes under {@code body.reflection}. */
interface ReflectionConfig {
  prompt?: string;
  promptDescription?: string;
  minLength?: number | null;
  placeholder?: string;
  submitLabel?: string;
}

/** The learner's stored answer, when the progress row carries one. */
function storedReflection(progress: MicroProgress | null | undefined): string | null {
  const v = progress?.result?.['reflectionResponse'];
  return typeof v === 'string' && v.trim().length > 0 ? v : null;
}

/**
 * Open-text reflection. One submission per learner: the answer rides the
 * progress row as {@code result.reflectionResponse} (the admin Reflections
 * report reads that key), so reopening the popup or reloading the page
 * shows the same words read-only instead of an empty box that would write
 * a second answer. Submitting is terminal — it closes the popup.
 */
function Reflection({
  item,
  config,
  progress,
  onComplete,
}: {
  item: RendererProps['item'];
  config: ReflectionConfig | undefined;
  progress: RendererProps['progress'];
  onComplete: RendererProps['onComplete'];
}) {
  const { t } = useTranslation('site-builder-blocks');
  const stored = storedReflection(progress);
  const [text, setText] = useState('');
  const [sent, setSent] = useState(false);
  // The progress row resolves after mount — seed the box once it does.
  useEffect(() => {
    if (stored != null) setText(stored);
  }, [stored]);

  const locked = sent || stored != null;
  const minLength = Math.max(0, config?.minLength ?? 0);
  const length = text.trim().length;
  const ready = length > 0 && length >= minLength;

  const submit = () => {
    if (locked || !ready) return;
    setSent(true);
    // The popup forwards the payload into its
    // progress.update({status: 'completed', ...}) write.
    onComplete({ result: { reflectionResponse: text.trim() } }, { close: true });
  };

  return (
    <>
      <PopupHeader kind={item.kind} title={item.title} description={item.description} />
      <div style={{ ...padded, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {config?.prompt && (
          <p style={{ margin: 0, fontWeight: 600, fontSize: 'var(--font-size-sm, 14px)' }}>
            {config.prompt}
          </p>
        )}
        {config?.promptDescription && (
          <p
            style={{
              margin: 0,
              fontSize: 'var(--font-size-sm, 14px)',
              color: 'var(--color-text-secondary, #71717a)',
            }}
          >
            {config.promptDescription}
          </p>
        )}
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          readOnly={locked}
          rows={5}
          placeholder={config?.placeholder || t('microInteractive.reflection.placeholder')}
          style={{
            padding: 12,
            border: '1px solid var(--color-border, rgba(0,0,0,0.12))',
            borderRadius: 8,
            fontSize: 'var(--font-size-sm, 14px)',
            fontFamily: 'var(--font-family-body, inherit)',
            resize: 'vertical',
            background: locked ? 'var(--color-surface-page, #f8fafc)' : undefined,
          }}
        />
        {locked ? (
          <p
            style={{
              margin: 0,
              fontSize: 'var(--font-size-xs, 12px)',
              fontWeight: 600,
              color: 'var(--color-success-500, #16a34a)',
            }}
          >
            {t('microInteractive.reflection.submitted')}
          </p>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={submit}
              disabled={!ready}
              style={{ ...btnPri, opacity: ready ? 1 : 0.5 }}
            >
              {config?.submitLabel || t('microInteractive.reflection.submit')}
            </button>
            {minLength > 0 && !ready && (
              <span
                style={{
                  fontSize: 'var(--font-size-xs, 12px)',
                  color: 'var(--color-text-secondary, #71717a)',
                }}
              >
                {t('microInteractive.reflection.minLength', { min: minLength, current: length })}
              </span>
            )}
          </div>
        )}
      </div>
    </>
  );
}

/**
 * Sort-into-buckets exercise over the canonical {@code body.dragDrop}
 * the admin editor writes: a pool of labelled items, one drop zone per
 * target, and {@code correctTargetId} as the answer key.
 *
 * <p>Two input paths, because the portal is mobile-first and HTML5 drag
 * events do not fire on touch: pointer users drag, everyone can instead
 * TAP an item to pick it up and tap a zone to drop it. Both write the
 * same placement map, so the two never disagree.
 *
 * <p>Answers are checked on demand rather than continuously — grading
 * each drop would give the key away by elimination. Completion fires
 * once every item sits in its correct zone.
 */
/** The learner's stored board — {@code result.dragDrop.placements},
 *  item id → target id — when every entry still names a real item and
 *  zone of this exercise. */
function storedDragDrop(
  progress: MicroProgress | null | undefined,
  itemIds: Set<string>,
  targetIds: Set<string>,
): Record<string, string> | null {
  const d = progress?.result?.['dragDrop'];
  const pl = d && typeof d === 'object' ? (d as { placements?: unknown }).placements : undefined;
  if (!pl || typeof pl !== 'object' || Array.isArray(pl)) return null;
  const entries = Object.entries(pl as Record<string, unknown>);
  if (entries.length === 0) return null;
  const ok = entries.every(([k, v]) => itemIds.has(k) && typeof v === 'string' && targetIds.has(v));
  return ok ? (pl as Record<string, string>) : null;
}

function DragDrop({
  item,
  exercise,
  progress,
  onComplete,
}: {
  item: RendererProps['item'];
  exercise: MicroDragDropExercise | undefined;
  progress?: MicroProgress | null;
  onComplete: RendererProps['onComplete'];
}) {
  const { t } = useTranslation('site-builder-blocks');
  /** itemId → targetId. Absent = still in the pool. */
  const [placements, setPlacements] = useState<Record<string, string>>({});
  /** Tap-to-place selection; null when nothing is picked up. */
  const [held, setHeld] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);
  const completeFiredRef = useRef(false);

  const targets = exercise?.targets ?? [];
  const items = exercise?.items ?? [];
  // Authors type target ids by hand, so an item can point at a zone that
  // no longer exists. Such an exercise is unwinnable — say so instead of
  // rendering a board that can never be completed.
  const targetIds = new Set(targets.map((tg) => tg.id));
  const configValid =
    targets.length > 0 &&
    items.length > 0 &&
    items.every((it) => it.id !== '' && targetIds.has(it.correctTargetId));

  // A completed row's stored board comes back read-only — see Matching.
  const stored = storedDragDrop(progress, new Set(items.map((it) => it.id)), targetIds);
  const done = progress?.status === 'completed' && stored != null;
  const storedKey = stored ? JSON.stringify(stored) : null;
  useEffect(() => {
    if (!done || storedKey == null) return;
    setPlacements(JSON.parse(storedKey) as Record<string, string>);
    setChecked(true);
    completeFiredRef.current = true;
  }, [done, storedKey]);

  if (!configValid) {
    return (
      <>
        <PopupHeader kind={item.kind} title={item.title} description={item.description} />
        <ErrorState message={t('microInteractive.dragDrop.invalid')} />
      </>
    );
  }

  const isKnownItem = (id: string) => items.some((it) => it.id === id);

  const place = (itemId: string, targetId: string | null) => {
    if (done) return;
    setChecked(false);
    setHeld(null);
    setPlacements((prev) => {
      if (targetId === null) {
        const { [itemId]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [itemId]: targetId };
    });
  };

  const pool = items.filter((it) => placements[it.id] == null);
  const allPlaced = pool.length === 0;
  const correctCount = items.filter((it) => placements[it.id] === it.correctTargetId).length;
  const allCorrect = correctCount === items.length;
  // Enabled the moment the pool is empty — including when the first
  // attempt is already right, which used to disable the button before it
  // could ever be clicked. Only a *checked* correct board retires it.
  const canCheck = allPlaced && !(checked && allCorrect);

  const check = () => {
    if (done) return;
    setChecked(true);
    if (allCorrect && !completeFiredRef.current) {
      completeFiredRef.current = true;
      onComplete({ result: { dragDrop: { placements } } });
    }
  };

  const isWrong = (itemId: string) =>
    checked &&
    placements[itemId] != null &&
    placements[itemId] !== items.find((i) => i.id === itemId)?.correctTargetId;

  const chip = (it: { id: string; label: string }, inZone: boolean) => (
    <button
      key={it.id}
      type="button"
      draggable={!done}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', it.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onClick={(e) => {
        // The chip sits inside its zone's click handler; without this a
        // tap would also drop whatever is currently held into that zone.
        e.stopPropagation();
        // Tapping a placed chip sends it home; tapping a pooled chip
        // picks it up (or puts it down again).
        if (done) return;
        if (inZone) place(it.id, null);
        else setHeld((h) => (h === it.id ? null : it.id));
      }}
      aria-pressed={held === it.id}
      aria-label={inZone ? t('microInteractive.dragDrop.returnToPool') : it.label}
      style={{
        padding: 'var(--space-2, 8px) var(--space-3, 12px)',
        borderRadius: 8,
        fontSize: 'var(--font-size-sm, 14px)',
        fontWeight: 500,
        textAlign: 'left',
        cursor: 'pointer',
        background: isWrong(it.id)
          ? 'rgba(220,38,38,0.08)'
          : checked && inZone
            ? 'rgba(22,163,74,0.08)'
            : 'var(--color-surface-card, #fff)',
        border: `1px solid ${
          held === it.id
            ? 'var(--color-brand-500, #7c3aed)'
            : isWrong(it.id)
              ? 'rgba(220,38,38,0.4)'
              : checked && inZone
                ? 'rgba(22,163,74,0.4)'
                : 'var(--color-border, rgba(0,0,0,0.12))'
        }`,
        boxShadow: held === it.id ? '0 0 0 2px rgba(124,58,237,0.2)' : 'none',
      }}
    >
      {it.label}
    </button>
  );

  return (
    <>
      <PopupHeader kind={item.kind} title={item.title} description={item.description} />
      <div style={{ ...padded, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {exercise?.instruction && (
          <p style={{ margin: 0, fontWeight: 600, fontSize: 'var(--font-size-sm, 14px)' }}>
            {exercise.instruction}
          </p>
        )}

        <p
          style={{
            margin: 0,
            fontSize: 'var(--font-size-xs, 12px)',
            color: 'var(--color-text-secondary, #71717a)',
          }}
        >
          {held
            ? t('microInteractive.dragDrop.placing', {
                label: items.find((i) => i.id === held)?.label ?? '',
              })
            : t('microInteractive.dragDrop.pickPrompt')}
        </p>

        {/* Pool */}
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const id = e.dataTransfer.getData('text/plain');
            if (isKnownItem(id)) place(id, null);
          }}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            minHeight: 52,
            padding: 10,
            borderRadius: 10,
            background: 'var(--color-surface-page, #f8fafc)',
            border: '1px dashed var(--color-border, rgba(0,0,0,0.12))',
          }}
        >
          {pool.length === 0 ? (
            <span
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                color: 'var(--color-text-tertiary, #94867d)',
                alignSelf: 'center',
              }}
            >
              {t('microInteractive.dragDrop.poolEmpty')}
            </span>
          ) : (
            pool.map((it) => chip(it, false))
          )}
        </div>

        {/* Targets */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {targets.map((target) => {
            const dropped = items.filter((it) => placements[it.id] === target.id);
            return (
              <div
                key={target.id}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const id = e.dataTransfer.getData('text/plain');
                  if (isKnownItem(id)) place(id, target.id);
                }}
                onClick={() => {
                  if (held) place(held, target.id);
                }}
                style={{
                  padding: 12,
                  borderRadius: 10,
                  border: `1px solid ${
                    held
                      ? 'var(--color-brand-500, #7c3aed)'
                      : 'var(--color-border, rgba(0,0,0,0.12))'
                  }`,
                  background: 'var(--color-surface-card, #fff)',
                  cursor: held ? 'pointer' : 'default',
                }}
              >
                <div
                  style={{
                    fontSize: 'var(--font-size-xs, 12px)',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 'var(--font-tracking-wider, 0.04em)',
                    color: 'var(--color-text-secondary, #71717a)',
                    marginBottom: 8,
                  }}
                >
                  {target.label || target.id}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, minHeight: 34 }}>
                  {dropped.length === 0 ? (
                    <span
                      style={{
                        fontSize: 'var(--font-size-xs, 12px)',
                        color: 'var(--color-text-tertiary, #94867d)',
                        alignSelf: 'center',
                      }}
                    >
                      {t('microInteractive.dragDrop.dropHere')}
                    </span>
                  ) : (
                    dropped.map((it) => chip(it, true))
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          {done && <SubmittedNote />}
          {!done && (
            <button
              type="button"
              onClick={check}
              disabled={!canCheck}
              style={{
                ...btnPri,
                opacity: canCheck ? 1 : 0.5,
                cursor: canCheck ? 'pointer' : 'not-allowed',
              }}
            >
              {checked && !allCorrect
                ? t('microInteractive.dragDrop.retry')
                : t('microInteractive.dragDrop.check')}
            </button>
          )}
          {!done && checked && (
            <span
              style={{
                fontSize: '13px',
                fontWeight: 600,
                color: allCorrect
                  ? 'var(--color-success-500, #16a34a)'
                  : 'var(--color-danger, #dc2626)',
              }}
            >
              {allCorrect
                ? t('microInteractive.dragDrop.allCorrect')
                : t('microInteractive.dragDrop.someWrong', {
                    correct: correctCount,
                    total: items.length,
                  })}
            </span>
          )}
        </div>
      </div>
    </>
  );
}

/** Read-only footer for an exercise the learner already completed. */
function SubmittedNote() {
  const { t } = useTranslation('site-builder-blocks');
  return (
    <span
      role="status"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: 'var(--space-2, 8px) var(--space-3, 12px)',
        borderRadius: 999,
        background: 'rgba(22,163,74,0.1)',
        color: 'var(--color-success-500, #16a34a)',
        fontSize: '13px',
        fontWeight: 600,
      }}
    >
      {t('microInteractive.submitted')}
    </span>
  );
}

const btnPri = {
  padding: 'var(--space-2, 8px) var(--space-4, 16px)',
  border: 'none',
  borderRadius: 8,
  background: 'var(--color-text-primary, #18181b)',
  color: 'var(--color-text-on-brand, #fff)',
  fontWeight: 600,
  fontSize: '13px',
  cursor: 'pointer',
};
